import {
  DataGrid as MUIDataGrid,
  GridColDef,
  GridSortModel,
  GridRowClassNameParams,
  GridRowIdGetter,
  GridColumnVisibilityModel,
  GridRowSelectionModel,
  GridRowModel,
} from '@mui/x-data-grid';
import DataGridWrapper from 'src/shared/components/organisms/dataGridWrapper';
import { Box, Divider, TablePagination } from '@mui/material';
import React from 'react';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import DataGridEmpty from './dataGridEmpty';

export type DataGridModel = {
  loading: boolean;
  totalRows: number;
  rowsPerPageOptions: number[];
  pageSize: number;
  page: number;
};

export type DataGridProps<T> = {
  columns: GridColDef[];
  rows: T[];
  autoHeight?: boolean;
  autoRowHeight?: boolean;
  dataModel: DataGridModel;
  checkboxSelection: boolean;
  disableSelectionOnClick?: boolean;
  onRowClick?: (params) => void; // (params: GridRowParams, event: MuiEvent<React.MouseEvent>, details: GridCallbackDetails) => void;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSelectionModelChange: (newSelectionModel) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (sortModel: GridSortModel) => void;
  selectionModel: GridRowSelectionModel;
  disablePointer?: boolean;
  mode?: 'server' | 'client';
  getRowClassName?: ((params: GridRowClassNameParams<any>) => string) | undefined;
  filterInToolbar?: React.ReactNode;
  getRowId?: GridRowIdGetter<any>;
  columnVisibilityModel?: GridColumnVisibilityModel;
  hidePagination?: boolean;
};

export const DataGrid = <T extends GridRowModel>(props: DataGridProps<T>) => {
  const {
    columns,
    rows,
    autoHeight = false,
    autoRowHeight = true,
    dataModel: { loading, totalRows, rowsPerPageOptions, pageSize, page },
    checkboxSelection,
    disableSelectionOnClick = false,
    onRowClick = undefined,
    onPageChange,
    onPageSizeChange,
    onSelectionModelChange,
    sortModel = undefined,
    onSortModelChange = undefined,
    selectionModel,
    disablePointer = false,
    mode = 'server',
    getRowClassName = undefined,
    filterInToolbar = undefined,
    getRowId = undefined,
    columnVisibilityModel = undefined,
    hidePagination = false,
  } = props;

  const handleSortModelChange = React.useCallback(
    (newSortModel: GridSortModel) => {
      if (onSortModelChange) {
        onSortModelChange(newSortModel);
      }
    },
    [onSortModelChange],
  );

  const CustomPagination = () => {
    if (!(totalRows > 0)) return null;
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: filterInToolbar ? 'space-between' : 'flex-end' }}>
          {filterInToolbar && filterInToolbar}
          <TablePagination
            component='div'
            onPageChange={(_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
              ConsoleHelper.log('newPage', newPage);
              onPageChange(newPage);
            }}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              onPageSizeChange(parseInt(event.target.value, 10));
            }}
            count={totalRows}
            page={page}
          />
        </Box>
        <Divider />
      </>
    );
  };

  return (
    <DataGridWrapper loading={loading}>
      <Box
        sx={{
          '& .MuiTablePagination-root': {
            display: totalRows > 0 ? 'flex' : 'none',
          },
        }}
      >
        <MUIDataGrid
          autoHeight={autoHeight}
          rows={rows}
          getRowId={getRowId}
          // initialState={{
          //   sorting: {
          //     sortModel: [
          //       {
          //         field: 'rating',
          //         sort: 'desc',
          //       },
          //     ],
          //   },
          // }}
          sx={{
            minHeight: 250,
            '& .MuiDataGrid-row': { minHeight: '2rem !important' },
            '& .lastColumnSeparator .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
              outline: 'none', // Ta bort blå outline på celler när man klickar på dem
            },
            ...{
              '& .MuiDataGrid-row .MuiDataGrid-cell ': {
                cursor: disablePointer ? null : 'pointer',
                overflowX: autoRowHeight ? 'clip !important' : null,
              },
            },
          }} // Annars syns inte DatGridEmpty-komponenten om no rows efter uppdatering av grid-paket /Sofie 2023-02-08
          columns={columns}
          density='compact'
          rowCount={totalRows}
          pageSizeOptions={rowsPerPageOptions}
          checkboxSelection={checkboxSelection}
          disableRowSelectionOnClick={disableSelectionOnClick}
          slots={{
            noRowsOverlay: loading ? undefined : DataGridEmpty,
            toolbar: hidePagination ? undefined : CustomPagination,
          }}
          initialState={{
            pagination: { paginationModel: { pageSize } },
          }}
          onRowClick={onRowClick ? (state) => onRowClick(state) : undefined}
          sortModel={sortModel || undefined}
          paginationMode={mode}
          keepNonExistentRowsSelected
          paginationModel={{ page, pageSize }}
          onRowSelectionModelChange={(newSelection) => onSelectionModelChange(newSelection)}
          rowSelectionModel={selectionModel}
          onPaginationModelChange={(paginationModel) => {
            if (page !== paginationModel.page) onPageChange(paginationModel.page);
            if (pageSize !== paginationModel.pageSize) onPageSizeChange(paginationModel.pageSize);
          }}
          onSortModelChange={onSortModelChange ? (state) => handleSortModelChange(state) : undefined}
          sortingMode={mode}
          getRowClassName={getRowClassName}
          columnVisibilityModel={columnVisibilityModel || undefined}
          getRowHeight={() => (autoRowHeight ? 'auto' : undefined)}
          hideFooterSelectedRowCount
          hideFooter={hidePagination}
        />
      </Box>
    </DataGridWrapper>
  );
};
